import { type FC, useEffect } from 'react';
import * as React from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'dibs-react-intl';
import { useFragment, graphql } from 'react-relay';
import { useIntl, defineMessages } from 'dibs-react-intl';
import AccountIcon from 'dibs-icons/exports/legacy/AccountOutlined.ts';
import MaskIcon from 'dibs-icons/exports/legacy/Mask.ts';
import MaskDirectLoginIcon from 'dibs-icons/exports/legacy/MaskDirectLogin.ts';
import { useHasUnreadNotifications } from './useHasUnreadNotifications.ts';
import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
} from 'dibs-tracking';
const { EVENT_NAVIGATION } = eventNameConstants;
const { INTERACTION_TYPE_GLOBAL_NAV } = interactionTypeConstants;
const { STEP_INTERACTION_GLOBAL_NAV_USER_ICON_CLICK, STEP_INTERACTION_IMPRESSION } =
    stepInteractionConstants;
import { Link } from 'dibs-elements/exports/Link.tsx';

import { type AccountNavTrigger_user$key } from './__generated__/AccountNavTrigger_user.graphql.ts';

import dibsCss from 'dibs-css';
import styles from './AccountNavTrigger-style.scss';

type AccountNavTriggerProps = {
    isMasquerading: boolean;
    isDirectLogin: boolean;
    user: AccountNavTrigger_user$key;
    hasRootPendingRefetch: boolean;
};

const messages = defineMessages({
    label: { id: 'AccountNavTrigger.label', defaultMessage: 'Account' },
    tradeLabel: { id: 'AccountNavTrigger.tradeLabel', defaultMessage: 'Rewards Dashboard' },
    myAccount: { id: 'AccountNavTrigger.MyAccount.name', defaultMessage: 'My Account' },
});

const AccountNavTrigger: FC<AccountNavTriggerProps> = ({
    isMasquerading,
    isDirectLogin,
    user: userRef,
    hasRootPendingRefetch,
}) => {
    const intl = useIntl();
    const user = useFragment(
        graphql`
            fragment AccountNavTrigger_user on User {
                profile {
                    firstName
                }
                isVerifiedTrade
                tradeFirmRewards @include(if: $hasTradeUserId) {
                    currentYearPoints
                    currentProgram {
                        programInfo {
                            displayName
                        }
                    }
                }
            }
        `,
        userRef
    );

    const {
        hasUnreadNotifications,
        isBomtOrderActionNotificationsVariant,
        unreadOrdersNotificationsOrderIds,
    } = useHasUnreadNotifications({ hasRootPendingRefetch });

    useEffect(() => {
        if (hasUnreadNotifications) {
            trackEvent({
                eventName: EVENT_NAVIGATION,
                interaction_type: INTERACTION_TYPE_GLOBAL_NAV,
                step_interaction_name: STEP_INTERACTION_IMPRESSION,
                trigger: `red dot impression ${
                    isBomtOrderActionNotificationsVariant ? 'variant' : 'control'
                }`,
                orderId: unreadOrdersNotificationsOrderIds.join(','),
            });
        }
    }, [
        hasUnreadNotifications,
        isBomtOrderActionNotificationsVariant,
        unreadOrdersNotificationsOrderIds,
    ]);

    let name: string | React.ReactElement;

    if (user?.profile?.firstName === undefined) {
        name = '';
    } else if (user?.profile?.firstName) {
        name = user?.profile?.firstName;
    } else {
        name = intl.formatMessage(messages.myAccount);
    }

    const { tradeFirmRewards, isVerifiedTrade } = user || {};
    const useTradeDisplay = !!tradeFirmRewards;
    const currentYearPoints = Math.floor(tradeFirmRewards?.currentYearPoints || 0);
    const programDisplayName = tradeFirmRewards?.currentProgram?.programInfo?.displayName || '';
    const linkTarget = isVerifiedTrade ? '/trade/rewards/' : '/my/account/';
    let maskIcon = null;
    if (isMasquerading) {
        maskIcon = (
            <span
                data-tn="user-menu-masq-icon"
                className={classnames(
                    styles.maskIcon,
                    dibsCss.cursorPointer,
                    dibsCss.inlineBlock,
                    dibsCss.mlXsmall
                )}
            >
                <MaskIcon />
            </span>
        );
    } else if (isDirectLogin) {
        maskIcon = (
            <span
                data-tn="user-menu-direct-login-icon"
                className={classnames(
                    styles.maskDirectLoginIcon,
                    dibsCss.relative,
                    dibsCss.mrXsmall
                )}
            >
                <MaskDirectLoginIcon />
            </span>
        );
    }

    const hasProgram = !!(isVerifiedTrade && programDisplayName);
    const hasPoints = currentYearPoints > 0;
    const pointsMessage = (
        <FormattedMessage
            id="dbl.Header.AccountNav.currentPoints"
            defaultMessage="{currentYearPoints, plural, one {# pt} other {# pts}}"
            values={{ currentYearPoints }}
        />
    );

    return (
        <div
            data-tn="user-menu-logged-in"
            className={classnames(
                styles.accountWrapper,
                dibsCss.itemsCenter,
                dibsCss.flex,
                dibsCss.cursorPointer,
                dibsCss.hFull
            )}
        >
            <div
                className={classnames(
                    useTradeDisplay && [
                        styles.nameWrapper,
                        dibsCss.flex,
                        dibsCss.flexCol,
                        dibsCss.itemsEnd,
                        dibsCss.justifyBetween,
                        dibsCss.wFull,
                    ]
                )}
            >
                <Link
                    buyerLinkType="contrastLinkNoEffect"
                    dataTn="user-menu-logged-in-name"
                    className={classnames(
                        styles.name,
                        useTradeDisplay
                            ? [styles.nameTrade, dibsCss.textSubHeaderMediumHeavy]
                            : [styles.name, dibsCss.textSubHeaderSmallNormal],
                        styles.tradeLink,
                        dibsCss.h18px,
                        dibsCss.textRight,
                        dibsCss.wAuto,
                        dibsCss.minW0,
                        isMasquerading || isDirectLogin
                            ? dibsCss.flex
                            : [dibsCss.truncate, dibsCss.block]
                    )}
                    href={linkTarget}
                >
                    {maskIcon}
                    {isMasquerading || isDirectLogin ? (
                        <span
                            className={classnames(dibsCss.truncate, dibsCss.wFull, dibsCss.minW0)}
                        >{`(${name})`}</span>
                    ) : (
                        name
                    )}
                </Link>
                <div className={classnames(dibsCss.wFull, dibsCss.minW0)}>
                    {(hasPoints || hasProgram) && (
                        <Link
                            buyerLinkType="contrastLinkNoEffect"
                            className={classnames(
                                styles.tradeStatus,
                                styles.tradeLink,
                                dibsCss.textSubHeaderSmallNormal,
                                dibsCss.block,
                                dibsCss.justifyEnd,
                                dibsCss.textRight,
                                dibsCss.wFull,
                                dibsCss.minW0,
                                dibsCss.mt2px,
                                dibsCss.truncate
                            )}
                            href={linkTarget}
                            dataTn="user-menu-firm-program"
                        >
                            {hasPoints && pointsMessage}
                            {hasPoints && hasProgram && ' | '}
                            {hasProgram && programDisplayName}
                        </Link>
                    )}
                </div>
            </div>
            <Link
                ariaLabel={intl.formatMessage(
                    isVerifiedTrade ? messages.tradeLabel : messages.label
                )}
                className={classnames(
                    styles.accountIcon,
                    dibsCss.relative,
                    dibsCss.inlineBlock,
                    dibsCss.minW18px,
                    dibsCss.h18px,
                    {
                        [styles.hasUnread]:
                            isBomtOrderActionNotificationsVariant && hasUnreadNotifications,
                    }
                )}
                href={linkTarget}
                onClick={event => {
                    const label = 'user-nav|user icon';
                    trackEvent(
                        {
                            category: 'navigation',
                            action: 'header navigation click',
                            label,
                            eventName: EVENT_NAVIGATION,
                            interaction_type: INTERACTION_TYPE_GLOBAL_NAV,
                            step_interaction_name: STEP_INTERACTION_GLOBAL_NAV_USER_ICON_CLICK,
                            trigger: label,
                        },
                        event
                    );
                }}
            >
                <AccountIcon />
            </Link>
        </div>
    );
};

export default AccountNavTrigger;
